import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

/**
 * IdleTimeoutProvider - Wraps authenticated pages to track user activity
 * and automatically logout after 8 hours of inactivity
 */
function IdleTimeoutProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const idleTimerRef = useRef(null);
  const isAuthenticatedRef = useRef(false);

  // Pages that don't need idle timeout (public pages)
  const publicPaths = ['/', '/signin', '/change-password'];

  const isPublicPage = useCallback(() => {
    return publicPaths.includes(location.pathname);
  }, [location.pathname]);

  const clearAuthTokens = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
  }, []);

  const handleIdleTimeout = useCallback(() => {
    // Clear tokens and redirect to signin
    clearAuthTokens();

    // Only redirect if not already on signin page
    if (location.pathname !== '/signin') {
      // Store a flag to show idle timeout message
      sessionStorage.setItem('idleLogout', 'true');
      navigate('/signin', { replace: true });
    }
  }, [clearAuthTokens, navigate, location.pathname]);

  const resetIdleTimer = useCallback(() => {
    // Clear existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Only set timer if user is authenticated (not on public pages)
    if (!isPublicPage() && isAuthenticatedRef.current) {
      idleTimerRef.current = setTimeout(() => {
        handleIdleTimeout();
      }, IDLE_TIMEOUT_MS);
    }
  }, [isPublicPage, handleIdleTimeout]);

  const handleUserActivity = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    isAuthenticatedRef.current = !!token;

    // Only set up idle tracking for authenticated users on protected pages
    if (!isPublicPage() && isAuthenticatedRef.current) {
      // Add event listeners for user activity
      ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, handleUserActivity, { passive: true });
      });

      // Start the initial timer
      resetIdleTimer();

      // Cleanup
      return () => {
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
        }
        ACTIVITY_EVENTS.forEach(event => {
          window.removeEventListener(event, handleUserActivity);
        });
      };
    }
  }, [isPublicPage, handleUserActivity, resetIdleTimer]);

  // Re-check auth status when location changes
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    isAuthenticatedRef.current = !!token;

    if (!isPublicPage() && isAuthenticatedRef.current) {
      resetIdleTimer();
    } else if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, [location.pathname, isPublicPage, resetIdleTimer]);

  return <>{children}</>;
}

export default IdleTimeoutProvider;
